import React, { useState } from 'react';
import { Card, Badge, Button, Form, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import { ALL_PERMISSIONS } from '../../../../shared/constants/permissions';

const PermissionsCard = ({ username, permissions, onPermissionsUpdated }) => {
    const { hasPermission } = useAuth();
    const service = useService();

    const canView   = hasPermission('users:viewPermissions');
    const canManage = hasPermission('users:managePermissions');

    const [isEditing, setIsEditing]   = useState(false);
    const [selected, setSelected]     = useState(permissions ?? []);
    const [isSaving, setIsSaving]     = useState(false);
    const [error, setError]           = useState(null);

    if (!canView) return null;

    const currentPerms = permissions ?? [];

    const handleToggle = (key) => {
        setSelected(prev =>
            prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
        );
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            await service.updateProfile(username, { permissions: selected });
            onPermissionsUpdated(selected);
            setIsEditing(false);
        } catch (err) {
            setError(err.message || 'Failed to update permissions');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setSelected(currentPerms);
        setIsEditing(false);
        setError(null);
    };

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                    <i className="bi bi-shield-lock me-2 text-primary"></i>
                    Permissions
                </h5>
                {canManage && !isEditing && (
                    <Button variant="outline-secondary" size="sm" onClick={() => { setSelected(currentPerms); setIsEditing(true); }}>
                        <i className="bi bi-pencil me-1"></i>Edit
                    </Button>
                )}
            </Card.Header>

            <Card.Body>
                {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                {isEditing ? (
                    <>
                        {ALL_PERMISSIONS.map(({ key, label, icon }) => {
                            const impliedByManage = key === 'users:viewPermissions' && selected.includes('users:managePermissions');
                            const isChecked = selected.includes(key) || impliedByManage;
                            return (
                                <Form.Check
                                    key={key}
                                    type="checkbox"
                                    id={`perm-${key}`}
                                    className="mb-2"
                                    checked={isChecked}
                                    disabled={impliedByManage}
                                    onChange={() => handleToggle(key)}
                                    label={
                                        <span>
                                            <i className={`bi ${icon} me-2 text-muted`}></i>
                                            {label}
                                            <code className="ms-2 text-muted" style={{ fontSize: '0.75em' }}>{key}</code>
                                            {impliedByManage && <span className="ms-2 text-muted" style={{ fontSize: '0.75em' }}>(implied)</span>}
                                        </span>
                                    }
                                />
                            );
                        })}
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
                                {isSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={handleCancel} disabled={isSaving}>
                                Cancel
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        {currentPerms.length === 0 ? (
                            <p className="text-muted mb-0">No special permissions</p>
                        ) : (
                            <div className="d-flex flex-wrap gap-2">
                                {ALL_PERMISSIONS.filter(p => currentPerms.includes(p.key)).map(({ key, label, icon }) => (
                                    <Badge key={key} bg="secondary" className="d-flex align-items-center gap-1 py-2 px-2">
                                        <i className={`bi ${icon}`}></i>
                                        {label}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

PermissionsCard.propTypes = {
    username: PropTypes.string.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    onPermissionsUpdated: PropTypes.func.isRequired,
};

export default PermissionsCard;
